<template>
  <div id="keyword-popular">
    <div class="page-header">
      <h2 class="page-title">인기 키워드</h2>
      <p class="page-desc">
        주제를 고르면 그 분야에서 <strong>많이 검색되는 키워드</strong>를 순위로 보여줍니다.
        글감을 정하지 못했을 때 여기서 시작하세요.
      </p>
    </div>

    <!-- 출처 전환: 네이버 연관 인기 vs 구글 실시간 트렌드 -->
    <div class="source-toggle">
      <button class="src-btn" :class="{ active: source === 'naver' }" @click="setSource('naver')">
        네이버 인기
      </button>
      <button class="src-btn" :class="{ active: source === 'realtime' }" @click="setSource('realtime')">
        실시간 트렌드 <em>구글</em>
      </button>
    </div>

    <!-- ============ 네이버 인기 ============ -->
    <template v-if="source === 'naver'">
    <section class="search-card">
      <div class="topic-chips">
        <button
          v-for="t in topics" :key="t"
          class="chip-btn" :class="{ active: seed === t }"
          @click="pick(t)"
        >{{ t }}</button>
      </div>
      <div class="seed-row">
        <input
          v-model="seed" class="seed-input" type="text"
          placeholder="직접 입력 (예: 캠핑)" maxlength="100" @keyup.enter="load"
        />
        <button class="btn-search" :disabled="loading" @click="load">
          {{ loading ? "조회 중…" : "순위 보기" }}
        </button>
      </div>

      <div class="mode-toggle">
        <button class="mt-btn" :class="{ active: !rising }" :disabled="loading" @click="setMode(false)">
          검색량순
        </button>
        <button class="mt-btn" :class="{ active: rising }" :disabled="loading" @click="setMode(true)">
          급상승순
        </button>
        <span class="mt-note">
          주제를 되풀이하는 말(근처맛집·식당·카페)은 빼고 <b>구체적인 글감</b>만 보여 줍니다.
          <b>지금 뜨는 것</b>을 보려면 급상승순으로.
        </span>
      </div>
    </section>

    <div v-if="errorMessage" class="notice notice-error">{{ errorMessage }}</div>
    <div v-if="loading" class="skeleton-card">인기 키워드를 불러오는 중…</div>

    <section v-else-if="result">
      <div class="result-summary">
        <span class="chip">주제 <b>{{ result.seed }}</b></span>
        <span class="chip">연관 키워드 <b>{{ result.totalRelated.toLocaleString() }}</b></span>
        <span class="chip">표시 <b>{{ result.rows.length }}</b></span>
      </div>

      <div class="notice notice-muted">
        네이버는 <b>인기 검색어 목록 API를 제공하지 않습니다</b>(실시간 검색어는 2021년 폐지).
        <template v-if="rising">
          이 순위는 <b>최근 4주 평균 ÷ 그 이전 평균</b>으로 계산한 급상승률입니다.
          검색량 상위 25개를 후보로 잡아 데이터랩으로 추이를 비교했습니다.
        </template>
        <template v-else>
          이 순위는 연관 키워드를 <b>월간 검색량순</b>으로 정렬한 것입니다.
          다만 <b>주제를 되풀이하는 말은 뺐습니다</b> — 「맛집」에서 근처맛집·주변맛집·강남맛집·식당·카페처럼
          다시 「맛집」을 말하는 것은 글감이 되지 못하면서 검색량만 커서 위를 다 차지했습니다.
        </template>
        속도를 위해 발행량·포화도는 조회하지 않습니다 — 키워드를 누르면 상세 분석으로 넘어갑니다.
      </div>

      <ol class="rank-list">
        <li v-for="(row, i) in result.rows" :key="row.keyword" class="rank-item">
          <span class="rk-no" :class="rankClass(i)">{{ i + 1 }}</span>
          <a class="rk-kw" href="#" @click.prevent="analyze(row.keyword)">{{ row.keyword }}</a>
          <span class="rk-vol">{{ row.totalVolume.toLocaleString() }}</span>
          <span class="rk-device">
            <i class="rk-mo" :style="{ width: mobileShare(row) + '%' }"></i>
          </span>
          <span class="rk-share">모바일 {{ mobileShare(row) }}%</span>
          <span v-if="rising" class="rk-growth" :class="growthClass(row.growth)">
            {{ growthLabel(row.growth) }}
          </span>
          <span v-else class="rk-comp" :class="compClass(row.competition)">{{ row.competition || "-" }}</span>
        </li>
      </ol>
    </section>

    <div v-else-if="!loading && !errorMessage" class="empty-state">
      주제를 고르거나 직접 입력해 보세요.
    </div>
    </template>

    <!-- ============ 실시간 트렌드 (구글) ============ -->
    <template v-else>
      <section class="search-card">
        <div class="notice notice-muted rt-intro">
          <b>구글에서 지금 뜨는 검색어</b>입니다(한국). 네이버가 실시간 검색어를 폐지해(2021) 그 공백을 메웁니다.
          <b>구글 트렌드라 네이버와 결이 다릅니다</b> — 시사·연예·스포츠 이슈 위주라, 「지금 뭐가 터졌나」 신호로 쓰세요.
          카테고리는 <b>자동 분류</b>라 일부 어긋날 수 있습니다. 항목을 누르면 네이버 검색량·포화도로 넘어갑니다.
        </div>
        <div v-if="rt" class="topic-chips">
          <button class="chip-btn" :class="{ active: rtCategory === 'all' }" @click="rtCategory = 'all'">
            전체 <em>{{ rt.items.length }}</em>
          </button>
          <button v-for="c in rt.categories" :key="c.code"
                  class="chip-btn" :class="{ active: rtCategory === c.code }" @click="rtCategory = c.code">
            {{ c.label }} <em>{{ c.count }}</em>
          </button>
        </div>
      </section>

      <div v-if="rtError" class="notice notice-error">{{ rtError }}</div>
      <div v-if="rtLoading" class="skeleton-card">실시간 트렌드를 불러오는 중…</div>

      <section v-else-if="rt && rt.items.length">
        <div class="result-summary">
          <span class="chip">출처 <b>구글 트렌드 · 한국</b></span>
          <span class="chip">표시 <b>{{ filteredTrends.length }}</b></span>
          <span class="chip" v-if="!rt.naverVolumeAvailable">네이버 검색량 미조회</span>
        </div>

        <ol class="rank-list rt-list">
          <li v-for="row in filteredTrends" :key="row.rank" class="rank-item rt-item">
            <span class="rk-no" :class="rankClass(row.rank - 1)">{{ row.rank }}</span>
            <div class="rt-main">
              <div class="rt-top">
                <a class="rk-kw" href="#" @click.prevent="analyze(row.keyword)">{{ row.keyword }}</a>
                <span class="rt-cat">{{ row.categoryLabel }}</span>
                <span v-if="row.approxTraffic" class="rt-traffic">구글 {{ row.approxTraffic }}</span>
                <span class="rt-naver" v-if="row.naverVolume != null">네이버 월 {{ row.naverVolume.toLocaleString() }}</span>
                <span class="rt-naver dim" v-else-if="rt.naverVolumeAvailable">네이버 검색량 낮음</span>
              </div>
              <a v-if="row.newsTitle" class="rt-news" :href="row.newsUrl || '#'" target="_blank" rel="noopener">
                {{ row.newsTitle }}<em v-if="row.newsSource"> · {{ row.newsSource }}</em>
              </a>
            </div>
          </li>
        </ol>
      </section>

      <div v-else-if="!rtLoading && !rtError" class="empty-state">
        실시간 트렌드를 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as keywordApi from "@/keyword/api";
import type { KeywordRow, KeywordSearchResult, RealtimeTrend } from "@/keyword/api";

export default defineComponent({
  name: "KeywordPopularView",
  data() {
    return {
      // 출처: naver(연관 인기) / realtime(구글 실시간). 마지막 선택을 브라우저에 기억.
      source: (localStorage.getItem("keyword.popular.source") as "naver" | "realtime") || "naver",
      // 실시간 트렌드 상태
      rt: null as RealtimeTrend | null,
      rtCategory: "all",
      rtLoading: false,
      rtError: "",
      // 블로그에서 흔히 다루는 주제. 씨앗이 넓을수록 그 분야 전반의 인기 키워드가 나온다.
      // ⚠️ 「전체」는 서버가 아래 주제를 모두 합쳐 준다.
      //    목록을 바꾸면 KeywordService.DEFAULT_TOPICS 도 같이 고칠 것.
      topics: ["전체", "맛집", "카페", "여행", "육아", "레시피", "운동", "인테리어", "뷰티", "캠핑", "반려동물"],
      seed: "맛집",
      // 검색량순 vs 급상승순. 검색량순은 언제나 범용 머리 키워드가 위를 차지한다.
      rising: false,
      loading: false,
      errorMessage: "",
      result: null as KeywordSearchResult | null,
    };
  },
  computed: {
    filteredTrends(): RealtimeTrend["items"] {
      if (!this.rt) return [];
      if (this.rtCategory === "all") return this.rt.items;
      return this.rt.items.filter((i) => i.category === this.rtCategory);
    },
  },
  methods: {
    setSource(s: "naver" | "realtime") {
      if (this.source === s) return;
      this.source = s;
      localStorage.setItem("keyword.popular.source", s);
      if (s === "realtime" && !this.rt) this.loadRealtime();
      if (s === "naver" && !this.result) this.load();
    },
    async loadRealtime() {
      this.rtLoading = true;
      this.rtError = "";
      try {
        const res: any = await keywordApi.getRealtimeTrends();
        const payload = res?.data;
        if (payload && Array.isArray(payload.items)) {
          this.rt = payload;
        } else if (res?.status === 403) {
          this.rtError = "키워드 리서치 사용 권한이 없습니다.";
        } else {
          this.rtError = res?.message || "실시간 트렌드 조회에 실패했습니다.";
        }
      } catch (e: any) {
        this.rtError = e?.message || "실시간 트렌드 조회 중 오류가 발생했습니다.";
      } finally {
        this.rtLoading = false;
      }
    },
    pick(t: string) {
      this.seed = t;
      this.load();
    },
    setMode(rising: boolean) {
      if (this.rising === rising) return;
      this.rising = rising;
      this.load();
    },
    growthLabel(g: number | null): string {
      if (g === null || g === undefined) return "-";
      const pct = Math.round((g - 1) * 100);
      return (pct >= 0 ? "▲" : "▼") + Math.abs(pct) + "%";
    },
    growthClass(g: number | null): string {
      if (g === null || g === undefined) return "";
      if (g >= 1.2) return "up-strong";
      if (g >= 1.0) return "up";
      return "down";
    },
    async load() {
      const seed = this.seed.trim();
      if (!seed) {
        this.errorMessage = "주제를 입력해 주세요.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const res: any = await keywordApi.getPopular(seed, 50, this.rising);
        const payload = res?.data;
        if (payload && Array.isArray(payload.rows)) {
          this.result = payload;
        } else if (res?.status === 403) {
          this.errorMessage = "키워드 리서치 사용 권한이 없습니다.";
          this.result = null;
        } else {
          this.errorMessage = res?.message || payload?.message || "조회에 실패했습니다.";
          this.result = null;
        }
      } catch (e: any) {
        this.errorMessage = e?.message || "조회 중 오류가 발생했습니다.";
        this.result = null;
      } finally {
        this.loading = false;
      }
    },
    /** 순위에서 상세 분석으로 */
    analyze(keyword: string) {
      this.$router.push({ path: "/keyword/search", query: { q: keyword } });
    },
    mobileShare(row: KeywordRow): number {
      if (row.totalVolume <= 0) return 0;
      return Math.round((row.mobileVolume / row.totalVolume) * 100);
    },
    rankClass(i: number): string {
      if (i === 0) return "gold";
      if (i === 1) return "silver";
      if (i === 2) return "bronze";
      return "";
    },
    compClass(c: string | null): string {
      if (c === "높음") return "bad";
      if (c === "중간") return "mid";
      if (c === "낮음") return "good";
      return "";
    },
  },
  created() {
    if (this.source === "realtime") this.loadRealtime();
    else this.load();
  },
});
</script>

<style lang="scss" scoped></style>
